import { z } from 'zod';

import { ZodBoTaskSnoozeUntilInput } from '@Common/types/bo/utils/zod_bo_task_snooze_until';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const mdlrBankNameFailedTaskApproveReasons = ['APPROVE_VALID_DOCUMENT'] as const;
export const ZodMdlrBankNameFailedTaskApproveReason = z.enum(mdlrBankNameFailedTaskApproveReasons);
export type MdlrBankNameFailedTaskApproveReason = z.infer<
  typeof ZodMdlrBankNameFailedTaskApproveReason
>;

export const mdlrBankNameFailedTaskRejectReasons = [
  'REJECTION_3RD_PARTY_ACCOUNT',
  'REJECTION_3RD_PARTY_ID',
  'REJECTION_FRAUD',
  'REJECTION_OTHER',
  'REJECTION_BLOCKED_DEPOSIT',
  'REJECTION_CANCELLED',
] as const;
export const ZodMdlrBankNameFailedTaskRejectReason = z.enum(mdlrBankNameFailedTaskRejectReasons);
export type MdlrBankNameFailedTaskRejectReason = z.infer<
  typeof ZodMdlrBankNameFailedTaskRejectReason
>;

export const mdlrBankNameFailedTaskRetryReasons = [
  'RETRY_INCOMPLETE_DOCUMENT',
  'RETRY_DOCUMENT_NOT_SUPPORTED',
  'RETRY_DOCUMENT_OLD_DOCUMENT',
  'RETRY_POOR_IMAGE_QUALITY',
  'RETRY_RANDOM_UPLOAD',
  'RETRY_OTHER',
  'RETRY_POI_EXPIRED',
  'RETRY_POI_INCOMPLETE',
  'RETRY_POI_NOT_SUPPORTED',
  'RETRY_POI_AND_RIB_CANNOT_BE_ACCEPTED',
  'RETRY_RIB_INCOMPLETE',
  'RETRY_NOT_SUPPORTED_DOCUMENT_NOT_RIB',
] as const;
export const ZodMdlrBankNameFailedTaskRetryReason = z.enum(mdlrBankNameFailedTaskRetryReasons);
export type MdlrBankNameFailedTaskRetryReason = z.infer<
  typeof ZodMdlrBankNameFailedTaskRetryReason
>;

export const mdlrNameFailedTaskReasonTexts: Record<TaskActionMdlrBankNameFailedReason, string> = {
  APPROVE_VALID_DOCUMENT: 'Approve - Valid document',
  RETRY_INCOMPLETE_DOCUMENT: 'Retry - Incomplete document',
  RETRY_DOCUMENT_NOT_SUPPORTED: 'Retry - Not supported document',
  RETRY_DOCUMENT_OLD_DOCUMENT: 'Retry - Old document',
  RETRY_POOR_IMAGE_QUALITY: 'Retry - Poor image quality',
  RETRY_RANDOM_UPLOAD: 'Retry - Random upload',
  RETRY_OTHER: 'Retry - Other',
  RETRY_POI_EXPIRED: 'Retry - POI expired',
  RETRY_POI_INCOMPLETE: 'Retry - POI incomplete',
  RETRY_POI_NOT_SUPPORTED: 'Retry - POI not supported',
  RETRY_POI_AND_RIB_CANNOT_BE_ACCEPTED: "Retry - POI and RIB can't be accepted",
  RETRY_RIB_INCOMPLETE: 'Retry - RIB incomplete',
  RETRY_NOT_SUPPORTED_DOCUMENT_NOT_RIB: 'Retry - Not supported document (not RIB)',
  REJECTION_3RD_PARTY_ACCOUNT: 'Reject - 3rd party account',
  REJECTION_3RD_PARTY_ID: 'Reject - 3rd party ID',
  REJECTION_FRAUD: 'Reject - Fraud',
  REJECTION_OTHER: 'Reject - Other',
  REJECTION_BLOCKED_DEPOSIT: 'Reject - User have blocked deposits restriction',
  REJECTION_CANCELLED: 'Reject - Cancelled',
};

export const mdlrBankNameFailedTaskReasons = [
  ...mdlrBankNameFailedTaskApproveReasons,
  ...mdlrBankNameFailedTaskRejectReasons,
  ...mdlrBankNameFailedTaskRetryReasons,
] as const;
export const ZodTaskActionMdlrBankNameFailedReason = z.enum(mdlrBankNameFailedTaskReasons);
export type TaskActionMdlrBankNameFailedReason = z.infer<
  typeof ZodTaskActionMdlrBankNameFailedReason
>;

const ZodPutMdlrBankNameFailedApproveRequest = z.object({
  reason: ZodMdlrBankNameFailedTaskApproveReason,
});

export type PutMdlrBankNameFailedApproveRequest = z.infer<
  typeof ZodPutMdlrBankNameFailedApproveRequest
>;

const ZodUserMessage = ZodTrimmedString.min(20).max(500);

const ZodPutMdlrBankNameFailedRejectRequest = z.object({
  reason: ZodMdlrBankNameFailedTaskRejectReason,
  user_message: ZodUserMessage.optional(),
});
export type PutMdlrBankNameFailedRejectRequest = z.infer<
  typeof ZodPutMdlrBankNameFailedRejectRequest
>;

const ZodPutMdlrBankNameFailedRetryRequest = z.object({
  reason: ZodMdlrBankNameFailedTaskRetryReason,
  snooze_until: ZodBoTaskSnoozeUntilInput,
  user_message: ZodUserMessage.optional(),
});

export type PutMdlrBankNameFailedRetryRequest = z.infer<
  typeof ZodPutMdlrBankNameFailedRetryRequest
>;

const ZodPutMdlrBankNameFailedActionResponse = z.object({
  id: ZodId,
});

export type PutMdlrBankNameFailedActionResponse = z.infer<
  typeof ZodPutMdlrBankNameFailedActionResponse
>;

export const putTasksMdlrBankNameFailedRejectIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutMdlrBankNameFailedRejectRequest,
  responseBody: ZodPutMdlrBankNameFailedActionResponse,
});

export type PutTasksMdlrBankNameFailedRejectIdEndpoint = GetEndpointType<
  typeof putTasksMdlrBankNameFailedRejectIdEndpoint
>;

export const putTasksMdlrBankNameFailedApproveIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutMdlrBankNameFailedApproveRequest,
  responseBody: ZodPutMdlrBankNameFailedActionResponse,
});

export type PutTasksMdlrBankNameFailedApproveIdEndpoint = GetEndpointType<
  typeof putTasksMdlrBankNameFailedApproveIdEndpoint
>;

export const putTasksMdlrBankNameFailedRetryIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutMdlrBankNameFailedRetryRequest,
  responseBody: ZodPutMdlrBankNameFailedActionResponse,
});

export type PutTasksMdlrBankNameFailedRetryIdEndpoint = GetEndpointType<
  typeof putTasksMdlrBankNameFailedRetryIdEndpoint
>;
